package xim.poc.game.configuration.v0.tactics.behaviors

import xim.poc.ActorManager
import xim.poc.ItemModelSlot
import xim.poc.ModelLook
import xim.poc.game.*
import xim.poc.game.StatusEffect.*
import xim.poc.game.configuration.SkillApplierHelper
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.TacticsInstinct
import xim.poc.game.configuration.v0.tactics.*
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.magicAttackBonus
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.move
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.pass
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.resistAll
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.resistSilence
import xim.poc.game.configuration.v0.tactics.TacticsInstincts.spontaneity
import xim.poc.game.event.ActorSkillChainEvent
import xim.poc.game.event.Event
import xim.resource.AbilityType
import xim.resource.table.AbilityInfoTable.toAbilityInfo
import kotlin.math.pow
import kotlin.time.Duration

private class ChainboundTarget(
    val target: ActorState,
    val step: Int,
)

class TacticsDisjoinedBehavior(actorState: ActorState): TacticsBehaviorController(actorState) {

    companion object {
        val unbridledSpells = setOf(spellThunderbolt_736, spellGatesofHades_739, spellTourbillion_740, spellUproot_747, spellMightyGuard_750, spellCesspool_752)
    }

    private var turnCounter = 0
    private var chainboundTarget: ChainboundTarget? = null

    override fun onBehaviorInitialized(): List<Event> {
        val look = ActorStateManager.player().getBaseLook().copy()

        look.equipment[ItemModelSlot.Head] = 388
        look.equipment[ItemModelSlot.Main] = 756
        look.equipment[ItemModelSlot.Sub] = 756

        actorState.setBaseLook(look)

        actorState.getUnit()
            .addInstinct(spontaneity, potency = 35)
            .addInstinct(magicAttackBonus, potency = 30)
            .addInstinct(resistSilence, potency = 100)
            .addInstinct(resistAll, potency = 25)

        return emptyList()
    }

    override fun onTurnStart(match: TacticsMatchInstance) {
        turnCounter += 1
    }

    override fun getEngagedState(): EngagedState.State {
        val actor = ActorManager[actorState.id]
        return if (actor == null || !actor.isReadyToDraw()) {
            EngagedState.State.Disengaged
        } else {
            EngagedState.State.Engaged
        }
    }

    override fun getSelectableSkills(): List<SkillId> {
        return if (actorState.hasStatusEffect(Spontaneity)) {
            getSkills().filter { it is SpellSkillId && !unbridledSpells.contains(it) }
        } else if (actorState.hasStatusEffect(UnbridledLearning)) {
            listOf(selectUnbridledSkill())
        } else if (actorState.getHpp() <= 0.66f && !hasUsedSkill(skillUnbridledWisdom_850)) {
            listOf(skillUnbridledWisdom_850)
        } else if (turnCounter > 4) {
            listOf(skillUnbridledLearning_810)
        } else {
            listOf(skillRequiescat_226, skillUrielBlade_238, skillGlorySlash_239)
        }
    }

    override fun estimateSkillValue(query: TacticsSkillQuery): Float {
        val isWeaponSkill = query.skill is AbilitySkillId && query.skill.toAbilityInfo().type == AbilityType.WeaponSkill
        val chainBonus = if (isWeaponSkill && query.result.primaryTargetId == chainboundTarget?.target?.id) { 1.5f } else { 1f }
        return chainBonus * super.estimateSkillValue(query)
    }

    override fun onSkillExecutedScoped(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext): List<Event> {
        val events = ArrayList<Event>()

        if (unbridledSpells.contains(primaryTargetContext.skill)) {
            actorState.consumeStatusEffectCharge(UnbridledLearning)
            turnCounter = 0
            chainboundTarget = null
        }

        if (primaryTargetContext.skill is SpellSkillId) {
            actorState.consumeStatusEffectCharge(BurstAffinity)
        }

        if (primaryTargetContext.skill is AbilitySkillId) {
            events += maybeSkillChain(primaryTargetContext, primaryTargetContext.skill)
        }

        return events
    }

    private fun maybeSkillChain(context: SkillApplierHelper.TargetEvaluatorContext, skill: AbilitySkillId): List<Event> {
        val current = chainboundTarget
        if (current == null || current.target.id != context.targetState.id) {
            chainboundTarget = ChainboundTarget(target = context.targetState, step = 1)
            return emptyList()
        }

        val next = ChainboundTarget(
            target = current.target,
            step = current.step + 1,
        ).also {
            chainboundTarget = it
        }

        val attribute = when (skill) {
            skillRequiescat_226 -> SkillChainAttribute.Gravitation
            skillUrielBlade_238 -> SkillChainAttribute.Fusion
            skillGlorySlash_239 -> SkillChainAttribute.Fragmentation
            else -> return emptyList()
        }

        val event = ActorSkillChainEvent(
            sourceId = context.sourceState.id,
            targetId = context.targetState.id,
            skillChainStep = SkillChainStep(step = next.step, attribute = attribute, window = Duration.ZERO),
            closingWeaponSkillDamage = 133,
            context = context.context,
        )

        return listOf(event)
    }

    private fun selectUnbridledSkill(): SpellSkillId {
        return unbridledSpells.shuffled().minBy { getSkillUsedCount(it) }
    }

}

class TacticsSelhteusBehavior(actorState: ActorState): TacticsBehaviorController(actorState) {

    override fun onBehaviorInitialized(): List<Event> {
        actorState.getUnit().instincts += listOf(
            TacticsInstinct(id = move.id, potency = 1),
            TacticsInstinct(id = pass.id, potency = 1),
        )

        return emptyList()
    }

    override fun getSelectableSkills(): List<SkillId> {
        return if (wantsToUseRejuvenation()) {
            listOf(mskillRejuvenation_3366)
        } else {
            super.getSelectableSkills() - mskillRejuvenation_3366
        }
    }

    override fun estimateSkillValue(query: TacticsSkillQuery): Float {
        val queryDistance = query.destinationPosition.distance(query.originalPosition)
        val desiredPosition = (getFriend()?.getCursorPosition() as? PanelPosition) ?: query.originalPosition
        val originalDesiredDistance = query.originalPosition.distance(desiredPosition)

        // Don't move if already in a desired spot
        if (originalDesiredDistance == 1 && queryDistance > 0) { return 0f }

        // Otherwise, minimize desired distance
        val distance = query.destinationPosition.distance(desiredPosition)
        val distanceMultiplier = 0.1f.pow(distance)
        return distanceMultiplier * super.estimateSkillValue(query)
    }

    private fun wantsToUseRejuvenation(): Boolean {
        if (hasUsedSkill(mskillRejuvenation_3366)) { return false }

        val friend = getFriend()
        if (friend != null && friend.getHpp() < 0.5f) { return true }

        return actorState.getHpp() < 0.5f
    }

    private fun getFriend(): ActorState? {
        return getMatch().getUnits().firstOrNull { it.getUnit().currentJob == tacticsJobId_DisjoinedOne }
    }

}

class TacticsSlashBehavior(actorState: ActorState, val look: ModelLook): TacticsBehaviorController(actorState) {

    override fun onBehaviorInitialized(): List<Event> {
        actorState.setBaseLook(look)
        return emptyList()
    }

    override fun getSelectableSkills(): List<SkillId> {
        return if (actorState.getHpp() < 0.5f) {
            listOf(mskillMegaflare_1295)
        } else {
            listOf(mskillFrizz_2871)
        }
    }

}